import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Grid,
  InputAdornment,
  Alert,
  Container,
  Card,
  CardContent,
  Paper,
  Box,
} from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import { ipayNet, newServer } from 'constants/constants';
import { fetchRequestByUser } from 'Helpers/request-by-user';
import './styles.css';
import { useNavigate } from 'react-router-dom';

/**
 * Este paso traia su PROPIO `createTheme` con hex a mano y `mode: 'dark'` fijo.
 * Dos problemas: duplicaba la paleta que ahora vive en `tokens.css`, y al fijar
 * el modo oscuro el paso seguia negro aunque el usuario eligiera tema claro.
 *
 * Se elimina ese tema local y el `ThemeProvider` que lo montaba: el paso hereda
 * el tema global, que ya se construye desde los tokens y si cambia de modo.
 */

/**
 * Dominio sobre el que se construyen los subdominios.
 * Antes el sufijo estaba escrito a mano en el `InputAdornment` y en el redirect.
 */
const PLATFORM_BASE_URL = process.env.REACT_APP_PLATFORM_BASE_URL || 'paymylawyer.cc';

/** Mismas reglas que `SUBDOMAIN_REGEX` en el backend, para fallar rapido en la UI. */
const SUBDOMAIN_REGEX = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$/;

const FundingStep = () => {
  const { user, logout } = useLogin();
  const [subDomain, setSubDomain] = useState('');
  const [submerchantid, setSubmerchatId] = useState('');
  const [loading, setLoading] = useState(false);
  /**
   * `checking` mientras se pregunta al API si el subdominio ya existe.
   * Antes `finished` arrancaba en `true`, así que un 400 (sin request) mostraba
   * el loading del 40% y nunca el formulario.
   */
  const [pageState, setPageState] = useState<'checking' | 'setup' | 'redirecting'>('checking');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [textIndex, setTextIndex] = React.useState(0);
  const navigate = useNavigate();

  const loadingTexts = [
    'Preparing Your Personalized Subdomain Experience...',
    "Great news! We're now setting up your exclusive subdomain web app. This process might take a few moments, so we appreciate your patience...",
    'Your tailored web experience is just around the corner!...',
  ];

  /**
   * Propósito: Decide si el subdominio ya está creado o hay que pedir el nombre.
   * Solo entra en `redirecting` con subdominio real; un 400 no significa "listo".
   */
  const loadFundingRequest = async () => {
    const result = await fetchRequestByUser(user);

    if (result.status === 'expired') {
      /**
       * Output:
       * Side effects — Cierra sesión; el token ya no sirve para crear el subdominio
       */
      logout();
      return;
    }

    if (result.status === 'ok') {
      const createdSubdomain = result.request.subdomain?.subdomain;
      if (createdSubdomain) {
        setSubDomain(createdSubdomain);
      }

      if (result.request.finished && createdSubdomain) {
        /**
         * Output:
         * Side effects — El portal ya existe: anima el progreso y redirige
         */
        setPageState('redirecting');
        return;
      }
    }

    /**
     * Output:
     * Side effects — Muestra el formulario de subdominio (postulación nueva o
     *   diferida sin portal todavía)
     */
    setPageState('setup');
  };

  /**
   * Propósito: Valida el nombre del subdominio y dispara la creación autenticada.
   */
  const saveData = async () => {
    setError(null);

    const normalized = subDomain.trim().toLowerCase();

    if (!normalized) {
      /**
       * Output:
       * Side effects — Error de validación; no llama al API
       */
      setError('Please enter a subdomain name.');
      return;
    }

    if (!SUBDOMAIN_REGEX.test(normalized)) {
      /**
       * Output:
       * Side effects — Error de formato; mismas reglas que el backend
       */
      setError(
        'Use only lowercase letters, numbers and hyphens, and start and end with a letter or number.',
      );
      return;
    }

    setLoading(true);
    try {
      await saveSubdomain(normalized);
      /**
       * Output:
       * Side effects — Subdominio creado o error ya pintado por saveSubdomain
       */
    } finally {
      setLoading(false);
    }
  };

  /**
   * Crea el subdominio contra el endpoint AUTENTICADO.
   *
   * Antes se llamaba a `subdomain/create-subdomain/:requestId`, que no pedia
   * token y confiaba en el `requestId` y el `submerchant_id` del body. Cualquiera
   * con el id de otra solicitud podia crearle un subdominio a un tercero.
   *
   * El endpoint nuevo resuelve la solicitud desde el token, provisiona el
   * submerchant si hace falta (flujo diferido) y devuelve JSON en vez de "OK".
   */
  const saveSubdomain = async (normalizedName: string) => {
    const response = await fetch(`${newServer}subdomain/create-subdomain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user?.token}`,
      },
      body: JSON.stringify({ subdomain: normalizedName }),
    });

    if (!response.ok) {
      // El backend responde texto plano con el motivo (nombre tomado, reservado,
      // formato invalido...), asi que se muestra tal cual en vez de un error generico.
      const detail = await response.text();
      /**
       * Output:
       * Side effects — Mantiene el formulario y muestra el motivo del 4xx/5xx
       */
      setError(detail || 'We could not create your subdomain. Please try again.');
      return;
    }

    const data = await response.json();
    const createdSubdomain = data?.subdomain || normalizedName;
    const submerchantId = data?.submerchant_id;

    setSubDomain(createdSubdomain);
    setSubmerchatId(submerchantId == null ? '' : String(submerchantId));
    setPageState('redirecting');

    if (selectedFile && submerchantId) {
      // El logo se sube despues: si falla, el subdominio ya quedo creado y no
      // tiene sentido bloquear al usuario por esto.
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);

      reader.onload = async () => {
        try {
          await fetch(`${newServer}aws/put-logo/`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': ipayNet.apiToken,
            },
            body: JSON.stringify({
              imageKeys: 'nothing',
              image: reader.result,
              idSubmerchant: submerchantId,
            }),
          });
        } catch (logoError) {
          console.log('logo upload failed:', logoError);
        }
      };
    }

    /**
     * Output:
     * Side effects — Sube el logo si hay archivo y navega a subscriptions
     */
    navigate(`/subscriptions/${submerchantId}/${createdSubdomain}`);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  useEffect(() => {
    loadFundingRequest();
  }, []);

  useEffect(() => {
    if (pageState !== 'redirecting') return;

    const targetSubdomain = subDomain;
    if (!targetSubdomain) {
      setPageState('setup');
      return;
    }

    const interval = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          clearInterval(interval);
          const baseUrl = process.env.REACT_APP_PLATFORM_BASE_URL || 'paymylawyer.cc';
          window.location.href = `https://${targetSubdomain}.${baseUrl}/login`;
          logout();
          navigate(`/subscriptions/${submerchantid}/${targetSubdomain}`);
          return 100;
        }
        return Math.min(oldProgress + 1, 100);
      });
    }, 100);

    const intervalId = setInterval(() => {
      setTextIndex((prevIndex) => (prevIndex + 1) % loadingTexts.length);
    }, 3000);

    return () => {
      clearInterval(interval);
      clearInterval(intervalId);
    };
  }, [pageState, subDomain]);

  return (
    <>
      {pageState === 'checking' ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </div>
      ) : pageState === 'redirecting' ? (
        /**
         * `#121212` fijo: en tema claro dejaba un bloque negro a pantalla
         * completa. Es la pantalla de redireccion al subdominio, asi que el
         * salto se veia justo al terminar el onboarding.
         */
        <div style={{ backgroundColor: 'var(--bg)', minHeight: '100vh', padding: '50px 0' }}>
          <div className='center'>
            <div className='ring'></div>
            {/* El color lo pone `.span` desde los tokens; antes venia inline en blanco puro. */}
            <span className='span'>Loading... {progress}%</span>
          </div>
          <Typography
            variant='h6'
            color='text.primary'
            style={{
              textAlign: 'center',
              marginTop: '20px',
              fontSize: '20px',
              fontWeight: 'bold',
            }}
          >
            {loadingTexts[textIndex]}
          </Typography>
        </div>
      ) : (
        <div
          className='bg-background'
          style={{
            minHeight: '100vh',
            paddingTop: '40px',
            paddingBottom: '40px',
          }}
        >
          <Container maxWidth='md'>
            {/* Sección de instrucciones */}
            {/*
              El gris oscuro fijo duplicaba el `background.paper` del tema: en
              claro se quedaba gris oscuro. Se quita y `Paper`/`Card` toman su
              superficie del tema; los bloques ANIDADOS suben a `surface-raised`
              para no quedar planos sobre la tarjeta.
            */}
            <Paper elevation={3} style={{ padding: '30px', marginBottom: '30px' }}>
              <Typography
                variant='h5'
                align='center'
                gutterBottom
                color='text.primary'
                style={{ fontWeight: 'bold' }}
              >
                Your Application is Awaiting Approval
              </Typography>
              {/* El gris fijo era un valor suelto; el texto secundario ahora es `text.secondary`. */}
              <Typography
                variant='body1'
                align='center'
                color='text.secondary'
                style={{ marginBottom: '20px' }}
              >
                Your application is currently pending approval. While you wait, you can set up your
                Subdomain Portal and explore the platform in <strong>TEST TRIAL MODE</strong>.
              </Typography>
              <Typography
                variant='body2'
                color='text.primary'
                style={{ marginBottom: '10px', fontWeight: 'bold' }}
              >
                Here's what you can do right now:
              </Typography>
              <ul style={{ paddingLeft: '20px', marginTop: '0', marginBottom: '20px' }}>
                <li>
                  <Typography variant='body2' color='text.secondary'>
                    Choose your Subdomain (e.g., <em>yourcompany.example.com</em>).
                  </Typography>
                </li>
                <li>
                  <Typography variant='body2' color='text.secondary'>
                    Upload your Company Logo.
                  </Typography>
                </li>
                <li>
                  <Typography variant='body2' color='text.secondary'>
                    Complete your Payment Portal Setup.
                  </Typography>
                </li>
              </ul>
              <Typography variant='body2' color='text.secondary'>
                We’ve issued you <strong>TEST KEYS</strong> to use the platform in TEST MODE. You
                can test all features and integrations safely while waiting for approval.
              </Typography>
            </Paper>

            <Card elevation={3}>
              <CardContent>
                <Typography
                  variant='h6'
                  align='center'
                  gutterBottom
                  color='text.primary'
                  style={{ margin: '20px 0', fontWeight: 'bold' }}
                >
                  Complete your Payment Portal Setup
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant='outlined'
                      style={{
                        /**
                         * Va en `style` y no en className: `Paper` ya declara su
                         * `background-color` desde el tema y ambas reglas tienen
                         * la misma especificidad, asi que la de MUI ganaria.
                         */
                        backgroundColor: 'var(--surface-2)',
                        padding: '20px',
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => document.getElementById('file-input')?.click()}
                    >
                      {selectedFile ? (
                        <img
                          src={URL.createObjectURL(selectedFile)}
                          alt='Uploaded logo'
                          style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px' }}
                        />
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <AddCircleOutline
                            fontSize='large'
                            color='action'
                            style={{ marginBottom: '10px' }}
                          />
                        </div>
                      )}
                      {/* `color='textSecondary'` es clave de MUI v4: en v5 no hace nada. */}
                      <Typography variant='subtitle1' color='text.secondary' gutterBottom>
                        Upload logo for your page
                      </Typography>
                      <input
                        type='file'
                        accept='.pdf, .jpg, .jpeg, .png'
                        id='file-input'
                        style={{ display: 'none' }}
                        onChange={handleFileInputChange}
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      variant='outlined'
                      style={{
                        backgroundColor: 'var(--surface-2)',
                        padding: '20px',
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant='subtitle1'
                        align='center'
                        color='text.secondary'
                        gutterBottom
                      >
                        Add the name of your subdomain for your account portal
                      </Typography>

                      {error && (
                        <Alert severity='error' onClose={() => setError(null)}>
                          {error}
                        </Alert>
                      )}

                      {/*
                        El campo forzaba a mano los tres estados del borde, el
                        color del texto y el del label. El tema ya resuelve todo
                        eso con los tokens (`border-strong`, `brand`,
                        `text-muted`), asi que se quitan los overrides para no
                        duplicar colores.
                      */}
                      <TextField
                        fullWidth
                        label='Subdomain name'
                        variant='outlined'
                        type='text'
                        margin='normal'
                        value={subDomain}
                        onChange={(e) => setSubDomain(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <span className='text-foreground-muted'>.{PLATFORM_BASE_URL}</span>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Paper>
                  </Grid>
                </Grid>

                <Box marginTop={4} textAlign='center'>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={saveData}
                    style={{
                      padding: '10px 20px',
                      fontSize: '16px',
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      // `color='inherit'` toma el contraste del boton en vez de blanco fijo.
                      <CircularProgress size={24} color='inherit' />
                    ) : (
                      'Continue'
                    )}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Container>
        </div>
      )}
    </>
  );
};

export default FundingStep;
